/// <amd-module name="Wiki/Search/WikiSuggestionsProvider" />
import * as React from "react";
import { ITfsPageService, IProjectData } from "Tfs/Platform/Page";
import { announce } from "VSS/Core/Util/Accessibility";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext, Services } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { routeUrl } from "VSS/Platform/Location";
import { IVssScenario } from "VSS/Platform/Performance";
import { IMenuItem, IMenuItemDetails, MenuCell, MenuItemType } from "VSSUI/Menu";
import { css } from "VSSUI/Util";
import { getFriendlyDisplayValue } from "VSSUI/Utilities/FriendlyNumber";
import { getSearchClient } from "Wiki/Clients/Search/RestClient/Search";
import { WikiSearchRequest, WikiSearchResponse, WikiResult } from "Wiki/Clients/Search/Contracts/Search.Shared";
import { BaseInstantSearchProvider } from "InstantSearch/Shared/InstantSearchProviders/BaseInstantSearchProvider";
import { IExecuteSearchService } from "InstantSearch/Shared/Services/ExecuteSearchService";
import { ResultRow } from "InstantSearch/Shared/ResultRow";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { IMenuServiceContext, IInstantSearchMenuService } from "InstantSearch/Shared/Types";
import { CustomerIntelligenceConstants } from "Wiki/Search/Constants";
import * as Resources from "Wiki/Search/Resources";

const maxResultRows = 5;

export interface WikiFetchError extends Error {
    activityId: string;
}

/**
 * Service for creating menu items for wiki search suggestions.
 */
export class WikiSuggestionsProvider extends BaseInstantSearchProvider<WikiSearchResponse> implements IInstantSearchMenuService<IMenuServiceContext> {
    private _contributionId: string = "ms.vss-wiki-web.wiki-suggestions-project-menu-provider";

    private _projectProperties: IProjectData | undefined;
    private _providerId: string = "";
    private _projectFilter: string = "";

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initContribution(this._contributionId);
    }

    protected _getMenuItems(menuId: string,
        context: IMenuServiceContext,
        invokeProvider: boolean,
        searchText: string
    ): IReadonlyObservableArray<IMenuItem> | null {
        if (invokeProvider && searchText) {
            this.getSuggestions(
                searchText,
                context,
                "WikiSuggestionsFetched",
                CustomerIntelligenceConstants.TtiSearchScenarioName,
                CustomerIntelligenceConstants.SubSequentSearchScenarioName
            );
        } else {
            this._menuItems.splice(0, this._menuItems.length);
        }

        return this._menuItems;
    }

    protected _initContribution(contributionId: string): void {
        super._initContribution(contributionId);

        if (this._contribution) {
            const { searchProjectFilter, searchProviderId } = this._contribution;
            this._providerId = searchProviderId ? searchProviderId : "";
            this._projectFilter = searchProjectFilter ? searchProjectFilter : "";
        }
    }

    protected _fetchResults(searchText: string, context: IMenuServiceContext): Promise<WikiSearchResponse> {
        const projectId = this.projectProperties ? this.projectProperties.id : "";
        const projectName = this.projectProperties ? this.projectProperties.name : "";
        const searchFilters: { [key: string]: string[] } = projectName && this._projectFilter ? { [this._projectFilter]: [projectName] } : {};
        const instantSearchRequest: WikiSearchRequest = {
            searchText: searchText,
            $skip: 0,
            $top: maxResultRows,
            $orderBy: [],
            filters: searchFilters,
            includeFacets: false
        };

        return getSearchClient(this.pageContext).fetchWikiSearchResults(instantSearchRequest, projectId);
    }

    protected _onFetchSuccess(
        searchText: string,
        context: IMenuServiceContext,
        data: WikiSearchResponse,
        scenario: IVssScenario
    ): void {
        const { searchInput, telemetryWriter, onFocus, invokedProvidersProps } = context;
        this._menuItems.splice(0, this._menuItems.length);

        if (this.shouldInvokeProvider(context) && !searchInput.queryText().trim()) {
            // Search text is empty. It got changed before fetching results. So, not updating menuItems.
            return;
        }

        const header = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.header
            : Resources.WikiHeader;
        const { results, count } = data;
        const searchResults = results;
        const totalCount = count;

        if (searchResults && searchResults.length > 0) {
            // Add header
            this._menuItems.push({
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            });

            // Add search results
            const addFocusOnFirstResult = searchResults.length === 1;
            searchResults.forEach((wikiResult, index) => {
                const title = this._getWikiPageHitDisplayName(wikiResult);

                this._menuItems.push({
                    id: "",
                    className: css(addFocusOnFirstResult && index === 0 && "focused"),
                    groupKey: this._groupKey,
                    rank: this._rank,
                    renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
                        details.onFocusItem = onFocus;
                        switch (menuCell) {
                            case MenuCell.PrimaryText:
                                return <ResultRow
                                    iconProps={{ iconName: 'TextDocument' }}
                                    title={title}
                                />;
                            default:
                                return undefined;
                        }
                    },
                    onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) =>
                        this._navigateToWikiPage(
                            wikiResult,
                            event,
                            event && (event.ctrlKey || event.metaKey),
                            () => this._recordWikiClickTelemetry(index, telemetryWriter))
                });
            });

            // Add row to view all wiki results on search portal
            if (totalCount > searchResults.length) {
                const title = format(Resources.ViewAllResults, getFriendlyDisplayValue(totalCount));

                this._menuItems.push({
                    id: "",
                    groupKey: this._groupKey,
                    rank: this._rank,
                    renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
                        details.onFocusItem = onFocus;
                        switch (menuCell) {
                            case MenuCell.PrimaryText:
                                return <ResultRow iconProps={{ iconName: "Search" }} title={title} />;
                            default:
                                return undefined;
                        }
                    },
                    onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) =>
                        this._executeSearch(
                            searchText,
                            this._providerId,
                            "wiki",
                            event.ctrlKey,
                            () => this._onViewAllResultsClick(maxResultRows, searchInput.setHelpVisibility, telemetryWriter))
                });
            }

            announce(searchResults.length === 1 ? Resources.SingleResultLoadedMessage : Resources.ResultsLoadedMessage);
        } else if (searchResults && searchResults.length === 0) {
            const text = this.projectProperties
                ? format(Resources.NoSearchSuggestionsInProjectMessage, Resources.WikiText, this.projectProperties.name)
                : format(Resources.NoSearchSuggestionsMessage, Resources.WikiText);

            // If this is quick navigation mode then show header as well
            if (invokedProvidersProps) {
                this._menuItems.push({
                    id: "",
                    groupKey: this._groupKey,
                    rank: this._rank,
                    itemType: MenuItemType.Header,
                    text: header,
                    className: "search-suggestion-header"
                });
            }

            // Update message
            this._menuItems.push({
                id: "",
                itemType: MenuItemType.Header,
                className: "suggestion-message",
                groupKey: this._groupKey,
                rank: this._rank,
                text: text
            });

            announce(text);
            telemetryWriter.publish("NoSuggestionsFound", { "type": "WikiInstantSearch" });
        }
    }

    protected _onFetchFail(context: IMenuServiceContext, error: WikiFetchError, scenario: IVssScenario): void {
        if (error.activityId) {
            scenario.addProperties({ responseActivityId: error.activityId });
        }

        const { invokedProvidersProps } = context;
        const header = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.header
            : Resources.WikiHeader;
        this._menuItems.splice(0, this._menuItems.length);
        this._menuItems.push(
            {
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            },
            {
                id: "",
                itemType: MenuItemType.Header,
                className: "suggestion-message",
                groupKey: this._groupKey,
                rank: this._rank,
                text: Resources.GenericErrorMessage
            }
        );

        announce(Resources.GenericErrorMessage);
    }

    private _navigateToWikiPage = (
        wikiResult: WikiResult,
        evt: React.KeyboardEvent<HTMLElement>,
        openInNewTab?: boolean,
        callback?: () => void
    ): void => {
        if (openInNewTab) {
            const url = this._getPageUrl(wikiResult)
            const newWindow = window.open(url, "_blank");
            if (newWindow && newWindow.opener) {
                newWindow.opener = null;
            }
        }
        else {
            onClickFPS(this.pageContext, this._getPageUrl(wikiResult), true, evt);
        }

        if (callback) {
            callback();
        }
    }

    private _getWikiPageHitDisplayName = (item: WikiResult): string => {
        const fixedWikiPageName = this._removeSpecialCharsFromWikiPath(item.fileName);
        const mdRemoved = this._removeExtensionfromPagePath(fixedWikiPageName);
        return decodeURIComponent(mdRemoved);
    }

    private _getPageUrl = (item: WikiResult): string => {
        const wikiPath: string = this._removeMappedPathFromWikiPath(item);
        const fixedWikiPath: string = this._removeSpecialCharsFromWikiPath(wikiPath);

        const routeValues: { [key: string]: string } = {
            wikiIdentifier: item.wiki.name,
            wikiVersion: "GB" + item.wiki.version,
            pagePath: fixedWikiPath,
            project: item.project.name
        };

        return routeUrl(this.pageContext, "ms.vss-wiki-web.wiki-overview-route", routeValues);
    }

    /** Wiki path returned by the service needs following modifications:
    1. Removal of file extension
    2. Space is encoded as hyphen by the service. It needs to be reverted back to space.
    3. Hyphen is encoded by the service to %2D. It needs to be reverted back to hyphen to prevent double encoding.
    **/
    private _removeSpecialCharsFromWikiPath = (wikiPath: string): string => {
        const HyphenRegExp = /-/g;
        const HyphenEncoding = "%2D";
        const Hyphen = "-";
        const WhiteSpace = " ";
        const fixedWikiPath: string = this._removeExtensionfromPagePath(wikiPath)
            .replace(HyphenRegExp, WhiteSpace)
            .replace(HyphenEncoding, Hyphen);

        return fixedWikiPath;
    }

    private _removeExtensionfromPagePath = (path: string): string => {
        const extensionStart = path.lastIndexOf(".");

        return ".md".toLocaleUpperCase().localeCompare(path.substring(extensionStart).toLocaleUpperCase(), navigator.language) === 0
            ? path.substring(0, extensionStart)
            : path;
    }

    private _removeMappedPathFromWikiPath = (item: WikiResult): string => {
        let mappedPath = item.wiki.mappedPath ? item.wiki.mappedPath : "/";
        if (!(mappedPath.length > 0 && mappedPath.charAt(mappedPath.length - 1) == "/")) {
            mappedPath = mappedPath.concat("/");
        }

        return item.path.substring(mappedPath.length - 1);
    }

    private _executeSearch = (queryText: string, searchProviderId?: string, entityType?: string, openInNewWindow?: boolean, callbackBeforeExecute?: () => void): void => {
        const executeSearchService = this.pageContext.getService<IExecuteSearchService>("IExecuteSearchService");
        executeSearchService.executeSearch(queryText, searchProviderId, entityType, openInNewWindow, callbackBeforeExecute);
    }

    private _recordWikiClickTelemetry = (index: number, telemetryWriter: TelemetryWriter): void => {
        telemetryWriter.publish("ResultClicked", {
            "index": index,
            "resultType": "WikiInstantSearch"
        });
    }

    private _onViewAllResultsClick = (index: number, setHelpVisibility: (hideHelp: boolean, focusInputOnShow?: boolean) => void, telemetryWriter: TelemetryWriter): void => {
        // Hide callout
        setHelpVisibility(true);

        // Record telemetry
        telemetryWriter.publish("ResultClicked", {
            "index": index,
            "resultType": "ViewAllWikiLink"
        });
    }

    private get projectProperties(): IProjectData | undefined {
        if (!this._projectProperties) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            this._projectProperties = tfsPageData ? tfsPageData.project : undefined;
        }

        return this._projectProperties;
    }
}

Services.add(
    "project-wiki-suggestions-provider-service",
    { serviceFactory: WikiSuggestionsProvider }
);
